import React, { useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { uploadSailingSchedule } from '../../lib/api/sailingschedule';
import { CircularProgress } from '@material-ui/core';
import Dropzone from 'react-dropzone';

interface UploadedFile {
    name: string;
    size: number;
    base64: string;
}

export interface MyDropzoneRef {
    handleProcess: () => Promise<boolean>;
}

interface MyDropzoneProps {
    onUploadStart?: () => void;
    onUploadComplete?: (success: boolean, message: string) => void;
}

export const MyDropzone = forwardRef<MyDropzoneRef, MyDropzoneProps>(({ onUploadStart, onUploadComplete }, ref) => {
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    console.log('[MyDropzone] render', { Dropzone: typeof Dropzone, hasDefault: !!Dropzone });

    const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: File[]) => {
        console.log('[MyDropzone] onDrop', { accepted: acceptedFiles.length, rejected: rejectedFiles.length, acceptedNames: acceptedFiles.map(f => f.name) });
        setSuccessMessage(null);
        setErrorMessage(null);
        for (const file of acceptedFiles) {
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    resolve(result.split(',')[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            setUploadedFiles(prev => [...prev, { name: file.name, size: file.size, base64 }]);
        }
    }, []);

    const handleProcess = async (): Promise<boolean> => {
        console.log('[MyDropzone] handleProcess', { fileCount: uploadedFiles.length });
        if (uploadedFiles.length === 0) {
            setErrorMessage('No files to upload');
            return false;
        }

        setIsUploading(true);
        setSuccessMessage(null);
        setErrorMessage(null);

        if (onUploadStart) {
            onUploadStart();
        }

        let successCount = 0;
        let errorCount = 0;

        for (const file of uploadedFiles) {
            try {
                await uploadSailingSchedule(file.base64, file.name);
                successCount++;
            } catch (error: any) {
                console.error('Upload error:', error);
                errorCount++;
            }
        }

        setIsUploading(false);

        if (successCount > 0 && errorCount === 0) {
            const message = `Successfully uploaded ${successCount} file(s)!`;
            setSuccessMessage(message);
            if (onUploadComplete) {
                onUploadComplete(true, message);
            }
            setUploadedFiles([]);
            return true;
        } else if (successCount > 0 && errorCount > 0) {
            const message = `Uploaded ${successCount} file(s), ${errorCount} failed`;
            setErrorMessage(message);
            if (onUploadComplete) {
                onUploadComplete(false, message);
            }
            return false;
        } else {
            const message = 'Upload failed';
            setErrorMessage(message);
            if (onUploadComplete) {
                onUploadComplete(false, message);
            }
            return false;
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    useImperativeHandle(ref, () => ({
        handleProcess
    }));

    const accept = {
        "application/vnd.ms-excel": [".xls"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    };

    return (
        <section className="container" style={{ width: '100%' }}>
            {successMessage && (
                <div style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    textAlign: 'center'
                }}>
                    {successMessage}
                </div>
            )}
            {errorMessage && (
                <div style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    textAlign: 'center'
                }}>
                    {errorMessage}
                </div>
            )}
            <Dropzone onDrop={onDrop} accept={accept as never}>
                {({ getRootProps, getInputProps, isDragActive }) => {
                    console.log('[MyDropzone] Dropzone children render', { hasGetRootProps: typeof getRootProps === 'function', hasGetInputProps: typeof getInputProps === 'function', isDragActive });
                    return (
                        <div
                            {...getRootProps({ className: 'dropzone' })}
                            style={{
                                border: `2px dashed ${isDragActive ? '#2196f3' : '#cccccc'}`,
                                borderRadius: '4px',
                                padding: '20px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: isDragActive ? '#e3f2fd' : '#fafafa',
                                minHeight: '150px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '20px',
                                transition: 'all 0.2s ease',
                                opacity: isUploading ? 0.6 : 1,
                                pointerEvents: isUploading ? 'none' : 'auto'
                            }}
                        >
                            <input {...getInputProps()} disabled={isUploading} />
                            {isUploading ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <CircularProgress size={24} />
                                    <span>Uploading files...</span>
                                </div>
                            ) : (
                                <p>{isDragActive ? 'Drop the Excel file here...' : 'Drag \'n\' drop Excel files here, or click to select'}</p>
                            )}
                        </div>
                    );
                }}
            </Dropzone>
            <aside>
                <h4 style={{ marginBottom: '10px', color: '#555' }}>Files:</h4>
                <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    maxHeight: '200px',
                    overflow: 'auto',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                }}>
                    {uploadedFiles.length > 0 ? (
                        uploadedFiles.map((file, index) => (
                            <li key={index} style={{
                                padding: '10px',
                                borderBottom: '1px solid #eee',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                opacity: isUploading ? 0.6 : 1,
                            }}>
                                <span>{file.name} - {file.size} bytes</span>
                                <button
                                    onClick={() => handleRemoveFile(index)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#f44336',
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        padding: '0 8px',
                                        opacity: isUploading ? 0.5 : 1,
                                    }}
                                    title="Remove file"
                                    disabled={isUploading}
                                >
                                    ×
                                </button>
                            </li>
                        ))
                    ) : (
                        <li style={{ color: '#999', padding: '10px' }}>No files selected</li>
                    )}
                </ul>
            </aside>
        </section>
    );
});